// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — globe slots (docs/AYNI_XR_MULTI_GLOBE_PLAN.md, phase 1).
 *
 * In a 2- or 4-globe layout every globe but the primary follows the
 * primary's date. A slot's *time rule* says how: `follow` (the same date,
 * as before), `offset` (the primary's date moved by whole months, years
 * or days: "the same month 30 years earlier"), or `fixed` (held on one
 * date whatever the primary does). The rule is applied to the primary's
 * running date before the sibling is located on its own frames
 * (`panelDateSync.panelLocate`), so everything downstream — pacing,
 * out-of-range dimming, the frame check — works unchanged on the
 * shifted date.
 *
 * The rules travel in links (`?g2=<dataset>&g2t=-30y`), in Khipu steps
 * (`globes[]`) and in a room's state, in the one short form `formatTimeRule`
 * prints and `parseTimeRule` reads: a signed count and a unit (`d`, `m`,
 * `y`), or `=` and a date for a fixed one. Pure; no DOM.
 */

/** A whole number of days, months or years, any sign. */
export interface TimeOffset {
  readonly years: number
  readonly months: number
  readonly days: number
}

export type GlobeTimeRule =
  | { readonly kind: 'follow' }
  | { readonly kind: 'offset'; readonly offset: TimeOffset }
  | { readonly kind: 'fixed'; readonly ms: number }

export const FOLLOW: GlobeTimeRule = { kind: 'follow' }

/** The short list offered first (owner, 2026-10-10): years back, and months ahead for seasons. */
export const OFFSET_PRESETS: readonly TimeOffset[] = [
  { years: -1, months: 0, days: 0 },
  { years: -10, months: 0, days: 0 },
  { years: -30, months: 0, days: 0 },
  { years: 0, months: 3, days: 0 },
  { years: 0, months: 6, days: 0 },
]

/** Most years an offset may move, either way: beyond this a typo, not a comparison. */
export const MAX_OFFSET_YEARS = 500

const DAY_MS = 86_400_000

export function offsetOf(years = 0, months = 0, days = 0): TimeOffset {
  return { years, months, days }
}

export function isZeroOffset(o: TimeOffset): boolean {
  return o.years === 0 && o.months === 0 && o.days === 0
}

export function sameRule(a: GlobeTimeRule, b: GlobeTimeRule): boolean {
  if (a.kind !== b.kind) return false
  if (a.kind === 'offset' && b.kind === 'offset') {
    return a.offset.years === b.offset.years && a.offset.months === b.offset.months && a.offset.days === b.offset.days
  }
  if (a.kind === 'fixed' && b.kind === 'fixed') return a.ms === b.ms
  return true
}

/**
 * A date moved by an offset, on the calendar (UTC): months and years keep
 * the day of the month where the month has it (31 Jan + 1 m → 28/29 Feb,
 * as `Date` clamps it here rather than rolling into March); days are
 * added after. Whole months, so a monthly stream shifted by −30 y lands
 * on the same month 30 years earlier, inside that frame's period.
 */
export function shiftDateMs(ms: number, offset: TimeOffset): number {
  if (!Number.isFinite(ms)) return ms
  const d = new Date(ms)
  const months = offset.years * 12 + offset.months
  if (months !== 0) {
    const day = d.getUTCDate()
    d.setUTCDate(1)
    d.setUTCMonth(d.getUTCMonth() + months)
    const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate()
    d.setUTCDate(Math.min(day, last))
  }
  return d.getTime() + offset.days * DAY_MS
}

/** The date a slot shows for the primary's date, under its rule. */
export function applyTimeRule(rule: GlobeTimeRule, primaryMs: number): number {
  if (rule.kind === 'follow') return primaryMs
  if (rule.kind === 'fixed') return rule.ms
  return shiftDateMs(primaryMs, rule.offset)
}

// --- the short form -----------------------------------------------------

const RULE_RE = /^([+-]?)(\d{1,5})([dmy])$/i
const FIXED_RE = /^=(\d{4})(?:-(\d{2})(?:-(\d{2}))?)?$/

/**
 * A rule from its short form: `-30y`, `+3m`, `5d` (plus assumed), `=2015`,
 * `=2015-06`, `=2015-06-01`; empty or `0` means follow. Null for anything
 * else, including an offset past `MAX_OFFSET_YEARS`.
 */
export function parseTimeRule(raw: string | null | undefined): GlobeTimeRule | null {
  const s = (raw ?? '').trim()
  if (!s || s === '0' || s === 'follow') return FOLLOW
  const fixed = FIXED_RE.exec(s)
  if (fixed) {
    const y = Number(fixed[1])
    const m = fixed[2] ? Number(fixed[2]) : 1
    const d = fixed[3] ? Number(fixed[3]) : 1
    if (m < 1 || m > 12 || d < 1 || d > 31) return null
    const ms = Date.UTC(y, m - 1, d)
    return Number.isFinite(ms) ? { kind: 'fixed', ms } : null
  }
  const match = RULE_RE.exec(s)
  if (!match) return null
  const n = Number(match[2]) * (match[1] === '-' ? -1 : 1)
  if (n === 0) return FOLLOW
  const unit = match[3].toLowerCase()
  const offset = unit === 'y' ? offsetOf(n) : unit === 'm' ? offsetOf(0, n) : offsetOf(0, 0, n)
  return offsetWithinLimits(offset) ? { kind: 'offset', offset } : null
}

/** The short form of a rule; the empty string for follow. */
export function formatTimeRule(rule: GlobeTimeRule): string {
  if (rule.kind === 'follow') return ''
  if (rule.kind === 'fixed') return `=${isoDay(rule.ms)}`
  const o = rule.offset
  // One unit at a time in the short form; a mixed offset is written in its finest unit.
  if (o.days !== 0) return signed(o.years * 365 + o.months * 30 + o.days) + 'd'
  if (o.months !== 0 || o.years === 0) return signed(o.years * 12 + o.months) + 'm'
  return signed(o.years) + 'y'
}

/** Whether an offset is inside the limit, whichever units it uses. */
export function offsetWithinLimits(o: TimeOffset): boolean {
  const years = Math.abs(o.years + o.months / 12 + o.days / 365)
  return Number.isFinite(years) && years <= MAX_OFFSET_YEARS
}

function signed(n: number): string {
  return n < 0 ? String(n) : `+${n}`
}

/** `YYYY-MM-DD` in UTC. */
export function isoDay(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10)
}

// --- links --------------------------------------------------------------

/** What a link says of one globe besides the primary: its dataset and its rule. */
export interface LinkedSlot {
  readonly dataset: string | null
  readonly time: GlobeTimeRule
}

/**
 * `g1`…`g4`: the dataset of globe N (1-based, by position); `g1t`…`g4t`:
 * its rule; `p`: the primary globe's number when it is not globe 1. The
 * primary's own dataset stays in `?dataset=` (or the `/dataset/` path),
 * as every link has it, so a link with no `g*` is what it always was.
 */
export const LINK_SLOT_MAX = 4
const PRIMARY_KEY = 'p'

const slotKey = (n: number) => `g${n}`
const ruleKey = (n: number) => `g${n}t`

/**
 * The globes a query string names, indexed from 0 as the panels are
 * (`[1]` is globe 2); the primary's slot is null (its dataset is the
 * link's own). A rule without a dataset is kept: it applies once
 * something is loaded there. Globes past the last named one are not
 * listed. `primary` is the primary's slot when the link says it is not
 * globe 1, else null.
 */
export function parseLinkedSlots(search: string | URLSearchParams): { slots: Array<LinkedSlot | null>; primary: number | null } {
  const params = search instanceof URLSearchParams ? search : new URLSearchParams(search)
  const p = Number(params.get(PRIMARY_KEY))
  const primary = Number.isInteger(p) && p >= 2 && p <= LINK_SLOT_MAX ? p - 1 : null
  const slots: Array<LinkedSlot | null> = []
  for (let n = 1; n <= LINK_SLOT_MAX; n++) {
    const dataset = params.get(slotKey(n))
    const rule = parseTimeRule(params.get(ruleKey(n)))
    const named = (dataset || params.has(ruleKey(n))) && n - 1 !== (primary ?? 0)
    slots.push(named ? { dataset: dataset || null, time: rule ?? FOLLOW } : null)
  }
  while (slots.length > 1 && slots[slots.length - 1] === null) slots.pop()
  return { slots, primary }
}

/**
 * The query string with these globes written in (the primary's left to
 * `?dataset=`), earlier `g*` and `p` parameters dropped. An empty or
 * follow rule is left out; an empty slot is left out.
 */
export function writeLinkedSlots(search: string, slots: ReadonlyArray<LinkedSlot | null>, primary = 0): string {
  const params = new URLSearchParams(search)
  for (let n = 1; n <= LINK_SLOT_MAX; n++) {
    params.delete(slotKey(n))
    params.delete(ruleKey(n))
  }
  params.delete(PRIMARY_KEY)
  slots.forEach((slot, i) => {
    const n = i + 1
    if (n > LINK_SLOT_MAX || !slot || i === primary) return
    if (slot.dataset) params.set(slotKey(n), slot.dataset)
    const rule = formatTimeRule(slot.time)
    if (rule) params.set(ruleKey(n), rule)
  })
  if (primary > 0 && primary < LINK_SLOT_MAX && slots.some(Boolean)) params.set(PRIMARY_KEY, String(primary + 1))
  const q = params.toString()
  return q ? `?${q}` : ''
}

/** Whether two globes' date ranges share any time (end exclusive), as the Real time list judges it. */
export function slotsShareDates(a: { startMs: number; endMs: number } | null, b: { startMs: number; endMs: number } | null): boolean | null {
  if (!a || !b) return null
  return a.startMs < b.endMs && b.startMs < a.endMs
}
